import { useCallback, useState } from 'react';
import { useExperiments, useTranslation } from '@wix/fe-essentials-standalone';
import { TemplateGalleryCard } from '@common/types';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import { useTemplateFavorites } from '@components/Templates/Favorites/useTemplateFavorites';
import { useAutoFavoriteFromQueryParam } from '@components/Templates/Favorites/useAutoFavoriteFromQueryParam';
import { FAVORITES_BI_CLICK_TYPES } from '@components/Templates/Favorites/useFavoritesBi';
import FavoriteButton from '@components/Templates/Favorites/FavoriteButton';
import StatusToastNotification from '@components/StatusToastNotification';
import { redirectToLoginWithFavorite } from '@utils/urlUtils';
import { ENABLE_TEMPLATE_FAVORITES_SPEC } from '@utils/constants';

export function useRecommendedFavoriteButton(
  templates?: TemplateGalleryCard[],
  isMobileView = false,
) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { userDetails, loading: isHeaderLoading } = useHeaderContext();
  const { experiments } = useExperiments({ readOnly: true });
  const { logBiClick } = useTemplateBi({ is_mobile: isMobileView });

  const shouldShowFavoriteButton = experiments.enabled(
    ENABLE_TEMPLATE_FAVORITES_SPEC,
  );

  const [failedToggleAction, setFailedToggleAction] = useState<
    'add' | 'remove' | null
  >(null);

  const {
    favoriteTemplateIds,
    toggleFavoriteTemplate,
    pendingFavoriteListingId,
    isTogglePending,
  } = useTemplateFavorites({
    enabled: shouldShowFavoriteButton && Boolean(userDetails?.email),
    onToggleError: (shouldFavorite) =>
      setFailedToggleAction(shouldFavorite ? 'add' : 'remove'),
  });

  const templateIds =
    templates?.map((t) => t.id).filter((id): id is string => Boolean(id)) ?? [];

  useAutoFavoriteFromQueryParam({
    enabled: shouldShowFavoriteButton && Boolean(userDetails?.email),
    toggle: (id, opts) =>
      toggleFavoriteTemplate({ listingId: id, shouldFavorite: true }, opts),
    templateIds,
  });

  const getFavoriteButton = useCallback(
    (card: TemplateGalleryCard) => {
      if (!shouldShowFavoriteButton || !card.id) return undefined;
      return (
        <FavoriteButton
          listingId={card.id}
          isFavorite={favoriteTemplateIds.has(card.id)}
          isLoading={
            isHeaderLoading ||
            (isTogglePending && pendingFavoriteListingId === card.id)
          }
          addLabel={t('templatesFavorites.gallery.tooltip.add')}
          removeLabel={t('templatesFavorites.gallery.tooltip.remove')}
          onToggle={(listingId, shouldFavorite) => {
            if (!userDetails?.email) {
              redirectToLoginWithFavorite(listingId, language);
              return;
            }
            toggleFavoriteTemplate({ listingId, shouldFavorite });
            logBiClick({
              clickType: shouldFavorite
                ? FAVORITES_BI_CLICK_TYPES.addToFavorites
                : FAVORITES_BI_CLICK_TYPES.removeFromFavorites,
            });
          }}
        />
      );
    },
    [
      favoriteTemplateIds,
      isHeaderLoading,
      isTogglePending,
      language,
      logBiClick,
      pendingFavoriteListingId,
      shouldShowFavoriteButton,
      t,
      toggleFavoriteTemplate,
      userDetails?.email,
    ],
  );

  const errorKeySuffix = isMobileView ? '.mobile' : '';
  const errorToast =
    failedToggleAction !== null ? (
      <StatusToastNotification
        key={failedToggleAction}
        status="error"
        message={t(
          `templatesFavorites.statusToast.${failedToggleAction}Failure${errorKeySuffix}`,
        )}
        onDismiss={() => setFailedToggleAction(null)}
        dismissible
        isMobileView={isMobileView}
      />
    ) : null;

  return {
    getFavoriteButton: shouldShowFavoriteButton ? getFavoriteButton : undefined,
    errorToast,
  };
}
